import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import PageLayout from '../common/PageLayout'
import Card from '../common/Card'
import { jobsApi, statusLabel } from '../../utils/accountApi'

// Icons
function CalendarIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <path d="M16 2v4M8 2v4M3 10h18"/>
    </svg>
  )
}

function ClockIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 6v6l4 2"/>
    </svg>
  )
}

function MapPinIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  )
}

function PlusIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 5v14M5 12h14"/>
    </svg>
  )
}

export default function ScheduledWorks() {
  const navigate = useNavigate()
  const [works, setWorks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedDay, setSelectedDay] = useState(null)

  const load = useCallback(async () => {
    try {
      setWorks(await jobsApi.upcoming())
      setError(null)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const getStatusColor = (status) => {
    switch (status) {
      case 'EN_ROUTE': return 'bg-blue-100 text-blue-800'
      case 'ASSIGNED': return 'bg-green-100 text-green-800'
      case 'SCHEDULED': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-slate-100 text-slate-800'
    }
  }

  const formatDate = (d) => new Date(d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
  const formatTime = (d) => new Date(d).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })

  const week = Array.from({ length: 7 }, (_, i) => {
    const date = new Date()
    date.setDate(date.getDate() - date.getDay() + i)
    return date
  })
  const sameDay = (a, b) => a && b && new Date(a).toDateString() === new Date(b).toDateString()
  const shown = selectedDay ? works.filter(w => sameDay(w.scheduled_start, selectedDay)) : works

  const headerAction = (
    <button onClick={() => navigate('/request')} title="Request a service" className="p-2 bg-slate-900 text-white rounded-xl touch-manipulation">
      <PlusIcon />
    </button>
  )

  return (
    <PageLayout
      title="Scheduled Works"
      subtitle="Upcoming visits from your professionals"
      headerAction={headerAction}
      onRefresh={load}
      showBackButton={true}
    >
      {/* Calendar Mini View */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900">This Week</h3>
          <CalendarIcon className="text-slate-500" />
        </div>

        <div className="grid grid-cols-7 gap-2 text-center">
          {week.map(date => (
            <div key={`h${date.getDay()}`} className="text-xs font-medium text-slate-500 py-2">
              {date.toLocaleDateString(undefined, { weekday: 'short' })}
            </div>
          ))}
          {week.map(date => {
            const hasWork = works.some(w => sameDay(w.scheduled_start, date))
            const selected = sameDay(selectedDay, date)
            return (
              <button
                key={date.toDateString()}
                onClick={() => setSelectedDay(selected ? null : date)}
                className={`p-2 rounded-lg text-sm touch-manipulation ${
                  selected ? 'ring-2 ring-slate-900 ' : ''
                }${hasWork ? 'bg-slate-900 text-white' : 'hover:bg-slate-100'}`}
              >
                {date.getDate()}
              </button>
            )
          })}
        </div>
      </Card>

      {/* Upcoming Works */}
      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900">
          {selectedDay ? `Works on ${formatDate(selectedDay)}` : 'Upcoming Works'}
        </h3>

        {loading && <Card><div className="text-sm text-slate-500">Loading…</div></Card>}
        {error && <Card><div className="text-sm text-red-600">Could not load your visits: {error}</div></Card>}
        {!loading && !error && shown.length === 0 && (
          <Card>
            <div className="text-center py-6">
              <p className="text-sm text-slate-600">{selectedDay ? 'Nothing scheduled that day.' : 'No upcoming visits.'}</p>
            </div>
          </Card>
        )}

        {shown.map(work => (
          <Card key={work.job_id} hover onClick={() => navigate(`/jobs/${work.job_id}`)}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-medium text-slate-900">{work.title}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(work.job_status)}`}>
                    {statusLabel(work.job_status)}
                  </span>
                </div>

                <div className="space-y-1 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <ClockIcon />
                    <span>{work.scheduled_start ? `${formatDate(work.scheduled_start)} at ${formatTime(work.scheduled_start)}` : 'Time to be confirmed'}</span>
                  </div>
                  {work.place && (
                    <div className="flex items-center gap-2">
                      <MapPinIcon />
                      <span>{work.place}</span>
                    </div>
                  )}
                  <div className="font-medium text-slate-900 mt-2">
                    {work.professional?.name || 'Professional to be assigned'}
                  </div>
                </div>
              </div>

              <span className="px-3 py-1 text-xs border border-slate-300 rounded-lg touch-manipulation">
                Details
              </span>
            </div>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card>
        <h3 className="font-semibold text-slate-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => navigate('/request')} className="p-3 border border-slate-300 rounded-xl text-sm font-medium hover:bg-slate-50 touch-manipulation">
            Request a Service
          </button>
          <button onClick={() => navigate('/orders')} className="p-3 border border-slate-300 rounded-xl text-sm font-medium hover:bg-slate-50 touch-manipulation">
            All Orders
          </button>
        </div>
      </Card>
    </PageLayout>
  )
}
